/**
 * AI Email Service
 * Generates personalized follow-up emails using Claude 3.5 Sonnet via backend Cloud Function
 */

import type { Lead } from '../types/lead';
import { sendEmailWithFirebase } from './emailService';

export interface EmailDraft {
  subject: string;
  body: string;
  generatedAt: Date;
  leadId: string;
  leadName: string;
  estimatedTokens?: number;
}

export interface EmailSendResult {
  success: boolean;
  messageId?: string;
  error?: string;
}

/**
 * Generate a follow-up email using AI via backend Cloud Function
 * Securely calls Claude API through backend (no CORS issues, no exposed API keys)
 */
export async function generateFollowupEmail(lead: Lead): Promise<EmailDraft> {
  try {
    // Try real API first via backend Cloud Function
    const apiUrl = import.meta.env.VITE_API_URL;
    
    if (apiUrl) {
      try {
        // Note: In production, include proper auth token from Firebase Auth
        const response = await fetch(`${apiUrl}/generateFollowupEmailAI`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            leadId: lead.id,
            leadName: lead.contactInfo.name,
            company: lead.contactInfo.company,
            role: lead.businessInfo.role,
            doorCount: lead.businessInfo.doorCount,
            currentSystem: lead.businessInfo.currentSystem,
            painPoints: lead.businessInfo.painPoints,
            status: lead.status,
            pipelineNotes: lead.pipelineNotes,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          return {
            subject: data.subject,
            body: data.body,
            generatedAt: new Date(data.generatedAt),
            leadId: data.leadId,
            leadName: lead.contactInfo.name,
          };
        }
      } catch (apiError) {
        console.warn('Backend AI API call failed, falling back to simulated response:', apiError);
      }
    }

    // Fallback to simulated response if real API unavailable
    const response = await simulateAIResponse(lead);
    return parseEmailResponse(response, lead);
  } catch (error) {
    console.error('Error generating follow-up email:', error);
    throw new Error('Failed to generate email: ' + (error instanceof Error ? error.message : 'Unknown error'));
  }
}

/**
 * Build AI prompt based on lead data and status
 */
function buildPrompt(lead: Lead): string {
  const productContext = getProductContext();
  const statusContext = getStatusContext(lead.status);
  
  return `You are a professional sales representative for ${productContext.name}.

PRODUCT CONTEXT:
${productContext.description}

LEAD INFORMATION:
- Name: ${lead.contactInfo.name}
- Company: ${lead.contactInfo.company || 'Not provided'}
- Role: ${lead.businessInfo.role || 'Not specified'}
- Property Size: ${lead.businessInfo.doorCount} doors
- Current System: ${lead.businessInfo.currentSystem || 'Unknown'}
- Pain Points: ${lead.businessInfo.painPoints?.join(', ') || 'Not specified'}
- Source: ${lead.source}
- Current Status: ${lead.status}
- Pipeline Notes: ${lead.pipelineNotes || 'No previous interactions recorded'}

CONTEXT FOR THIS FOLLOW-UP:
${statusContext}

INSTRUCTIONS:
1. Write a personalized, professional follow-up email
2. Keep it concise: 100-150 words maximum
3. Use Canadian English spelling and tone
4. Be friendly but professional
5. Reference their specific situation (doors, pain points, etc.)
6. Include a clear, soft call-to-action
7. DO NOT mention internal CRM fields like "status" or "pipeline"
8. DO NOT be pushy or overly salesy
9. DO NOT use emojis or excessive punctuation

FORMAT YOUR RESPONSE EXACTLY AS:
SUBJECT: [Your subject line here]

[Email body here]

Remember: This is a follow-up, not a cold email. Show you remember previous context.`;
}

/**
 * Get product context for the current project
 */
function getProductContext() {
  return {
    name: 'DossiLoc (RentFlow)',
    description: `DossiLoc is an AI-powered rent collection automation platform designed specifically for Canadian property managers and landlords. 

Key Features:
- Automated rent collection via email money transfer (Interac)
- Gmail integration for zero-inbox rent management
- Tenant portal for self-service
- Automatic late payment reminders
- Real-time payment tracking
- Canadian banking integration

Target Market: Property managers with 10-250+ doors looking to eliminate manual rent collection work and improve cash flow.

Value Proposition: Save 10-15 hours/month on rent collection, reduce late payments by 30-50%, never miss a payment, complete automation.`,
  };
}

/**
 * Get status-specific context for email generation
 */
function getStatusContext(status: string): string {
  const contexts: Record<string, string> = {
    new: 'This is an initial follow-up to gauge interest. Keep it light and exploratory.',
    
    contacted: 'You\'ve made initial contact. This follow-up should reference the previous conversation and move toward discovery or demo.',
    
    qualified: 'They\'ve expressed interest and are qualified. Focus on scheduling a demo or discovery call.',
    
    demo_scheduled: 'Demo is scheduled. This is a pre-demo follow-up to confirm details and ensure they\'re prepared.',
    
    proposal_sent: 'You\'ve sent a proposal. This is a gentle follow-up to check if they have questions and gauge decision timeline. Be consultative, not pushy.',
    
    negotiation: 'You\'re in negotiation. Address any concerns, highlight value, and work toward closing. Be collaborative.',
    
    nurture: 'This lead isn\'t ready now but has potential. Provide value without asking for anything. Share insights, case studies, or helpful tips related to their pain points.',
  };

  return contexts[status.toLowerCase()] || 'This is a general follow-up to re-engage the lead and understand their current situation.';
}

/**
 * Parse AI response into subject and body
 */
function parseEmailResponse(response: string, lead: Lead): EmailDraft {
  try {
    const lines = response.split('\n');
    let subject = '';
    let bodyLines: string[] = [];
    let foundSubject = false;

    for (const line of lines) {
      if (line.trim().toUpperCase().startsWith('SUBJECT:')) {
        subject = line.replace(/^SUBJECT:/i, '').trim();
        foundSubject = true;
      } else if (foundSubject && line.trim()) {
        bodyLines.push(line);
      }
    }

    // Fallback if parsing fails
    if (!subject) {
      subject = getFallbackSubject(lead);
    }
    if (bodyLines.length === 0) {
      bodyLines = [getFallbackBody(lead)];
    }

    return {
      subject,
      body: bodyLines.join('\n').trim(),
      generatedAt: new Date(),
      leadId: lead.id,
      leadName: lead.contactInfo.name,
    };
  } catch (error) {
    console.error('Error parsing email response:', error);
    
    // Return fallback email
    return {
      subject: getFallbackSubject(lead),
      body: getFallbackBody(lead),
      generatedAt: new Date(),
      leadId: lead.id,
      leadName: lead.contactInfo.name,
    };
  }
}

/**
 * Get fallback subject line
 */
function getFallbackSubject(lead: Lead): string {
  const subjects: Record<string, string> = {
    new: `Following up on DossiLoc for ${lead.contactInfo.company || 'your properties'}`,
    contacted: 'Quick follow-up on rent automation',
    qualified: 'Ready to see DossiLoc in action?',
    demo_scheduled: 'Looking forward to our demo',
    proposal_sent: 'Checking in on our proposal',
    negotiation: 'Next steps for DossiLoc implementation',
    nurture: 'Helpful insights for property management',
  };

  return subjects[lead.status.toLowerCase()] || 'Following up on DossiLoc';
}

/**
 * Get fallback email body
 */
function getFallbackBody(lead: Lead): string {
  return `Hi ${lead.contactInfo.name.split(' ')[0]},

I wanted to follow up regarding DossiLoc for ${lead.contactInfo.company || 'your properties'}.

With ${lead.businessInfo.doorCount} doors, automating your rent collection could save you significant time each month while improving your cash flow.

Would you be open to a brief conversation to explore how DossiLoc could help streamline your operations?

Best regards,
The DossiLoc Team`;
}

/**
 * Simulate AI response for development/testing
 * Fallback for when API key is not configured
 */
async function simulateAIResponse(lead: Lead): Promise<string> {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500));

  const firstName = lead.contactInfo.name.split(' ')[0];
  const company = lead.contactInfo.company || 'your properties';
  const doors = lead.businessInfo.doorCount;

  const responses: Record<string, string> = {
    new: `SUBJECT: Automating rent collection for ${company}

Hi ${firstName},

I noticed you inquired about DossiLoc for managing ${doors} doors. I wanted to reach out and see if you'd like to learn more about how we're helping Canadian property managers eliminate manual rent collection.

Our clients typically save 10-15 hours per month and reduce late payments by 30-50% through complete automation.

Would you be open to a quick 15-minute call to see if DossiLoc might be a good fit for ${company}?

Best regards,
The DossiLoc Team`,

    contacted: `SUBJECT: Next steps for ${company}

Hi ${firstName},

Thanks for our conversation about DossiLoc. I wanted to follow up and see if you had any questions about automating rent collection for your ${doors} doors.

Based on what you shared about ${lead.businessInfo.painPoints?.[0] || 'manual processes'}, I think a quick demo would be valuable to show you exactly how DossiLoc can help.

Would next week work for a 20-minute walkthrough?

Best regards,
The DossiLoc Team`,

    qualified: `SUBJECT: Ready to see DossiLoc in action?

Hi ${firstName},

I'd love to show you how DossiLoc works for property managers like yourself with ${doors} doors.

The demo is straightforward – we'll walk through the automated rent collection workflow, tenant portal, and reporting features. Most clients find it eye-opening to see how much time they can reclaim.

Do you have 20-30 minutes this week or next for a personalized demo?

Best regards,
The DossiLoc Team`,

    demo_scheduled: `SUBJECT: Preparing for our DossiLoc demo

Hi ${firstName},

Looking forward to showing you DossiLoc in action! I've scheduled our demo and wanted to make sure we make the best use of our time together.

It would be helpful to understand:
- Your current rent collection process
- Biggest pain points with ${doors} doors
- Any specific features you want to see

Feel free to reply with any questions before we meet!

Best regards,
The DossiLoc Team`,

    proposal_sent: `SUBJECT: Checking in on the DossiLoc proposal

Hi ${firstName},

I wanted to follow up on the proposal I sent for automating rent collection at ${company}.

I know evaluating new systems is a big decision. If you have any questions about pricing, implementation, or how DossiLoc would work with your ${doors} doors, I'm happy to jump on a quick call.

Is there anything I can clarify or any concerns I can address?

Best regards,
The DossiLoc Team`,

    negotiation: `SUBJECT: Moving forward with DossiLoc

Hi ${firstName},

I appreciate you taking the time to review everything. I wanted to address any remaining questions about implementing DossiLoc for ${company}.

For ${doors} doors, our clients typically see ROI within 2-3 months through time savings and improved cash flow. We can also customize the onboarding timeline to fit your needs.

What would help you feel confident moving forward?

Best regards,
The DossiLoc Team`,

    nurture: `SUBJECT: Quick tip for managing ${doors} doors

Hi ${firstName},

I came across an insight that might be helpful for managing ${company}: Many property managers find that automated reminders reduce late payments by 30-40% without any additional effort.

Even if you're not ready for full automation, simple automated email reminders 3 days before rent is due can make a significant difference in cash flow.

No pressure – just thought this might be useful!

Best regards,
The DossiLoc Team`,
  };

  return responses[lead.status.toLowerCase()] || responses.new;
}

/**
 * Call Anthropic API for real AI email generation
 * NOTE: This function is disabled in favor of backend Cloud Functions
 * Direct API calls from frontend cause CORS issues and expose API keys
 * 
 * To implement real AI generation:
 * 1. Create a backend Cloud Function that calls Anthropic API
 * 2. Call that function from frontend instead
 * 3. Never expose API keys in frontend code
 * 
 * This function kept for reference only.
 */
async function callAnthropicAPI(prompt: string): Promise<string> {
  throw new Error('Real API calls should be made through backend Cloud Functions, not from frontend. This causes CORS errors and exposes API keys.');
}

/**
 * Batch generate emails for multiple leads
 */
export async function generateBulkEmails(leads: Lead[]): Promise<EmailDraft[]> {
  const drafts: EmailDraft[] = [];
  
  for (const lead of leads) {
    try {
      const draft = await generateFollowupEmail(lead);
      drafts.push(draft);
    } catch (error) {
      console.error(`Failed to generate email for lead ${lead.id}:`, error);
      // Continue with other leads
    }
  }
  
  return drafts;
}

/**
 * Send email via Firebase Trigger Email Extension
 */
export async function sendEmail(
  to: string,
  subject: string,
  body: string,
  from?: string,
  leadId?: string,
  generatedByAI?: boolean,
  sentBy?: string,
  sentByName?: string
): Promise<EmailSendResult> {
  try {
    // Use Firebase extension for server-side email delivery
    const result = await sendEmailWithFirebase({
      to,
      subject,
      body,
      from: from || 'noreply@dossiloc.ca',
      fromName: 'DossiLoc Sales Team',
      leadId,
      generatedByAI,
      sentBy,
      sentByName,
    });
    
    return result;
  } catch (error) {
    console.error('Error sending email:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}
